import { useEffect, useState } from 'react';
import Loading from '../../../components/Loading';
import { colors } from '../../../assets/colors';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import Row from '../../../components/layout/Row';
import Input from '../../../components/Input';
import ButtonLoading from '../../../components/ButtonLoading';
import { formatDatePtBr } from '../../../utils/dateUtils';
import { createInvite, getInvitesSent } from '../../../api/invites';
import { getUserByEmail } from '../../../api/user';
import { toast } from 'react-toastify';
import { USER_DATA } from '../../../utils/constants';
import { useParams } from 'react-router-dom';
import EmptyState from '../../../components/EmptyState';
import { ProjectsTypes } from '../../../interfaces/ProjectsTypes';
import Badge from '../../../components/Badge';
import { getInviteEmail } from '../../../utils/inviteEmail';
import { mountHistory } from '../../../utils/history';
import { createHistory } from '../../../api/history';
import { ActionTypeEnum } from '../../../interfaces/HistoryTypes';

interface Props {
    currentProject: ProjectsTypes
}

export default function ConfigTab({ currentProject }: Props) {

    const { id } = useParams()

    const userOwnerProject = JSON.parse(localStorage.getItem(USER_DATA) || '{}').uid === currentProject.main_user_uuid

    const [loading, setLoading] = useState(true);
    const [loadingSendInvite, setLoadingSendInvite] = useState(false);
    const [invites, setInvites] = useState([]);
    const [userEmail, setUserEmail] = useState('');
    const [userName, setUserName] = useState('');

    useEffect(() => {
        setLoading(true);
        fetchInvites();
    }, [])

    const fetchInvites = async () => {
        if (id) {
            getInvitesSent(JSON.parse(localStorage.getItem(USER_DATA) || '{}').uid, id)
                .then(async (response: any) => {
                    await response.sort((a: any, b: any) => {
                        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                    })
                    setInvites(response)
                })
                .finally(() => {
                    setLoading(false);
                    setLoadingSendInvite(false);
                })
        } else {
            setLoading(false);
            setLoadingSendInvite(false);
        }
    }

    const sendInvite = async () => {

        setLoadingSendInvite(true);

        if (!userEmail) {
            toast.error('Digite o email para enviar um convite.');
            setLoadingSendInvite(false);
            return
        }

        const userEmailExists = invites.some((invite: any) => invite.user_to_email === userEmail && invite.status == 'pending')

        if (userEmailExists) {
            toast.error('Usuário já tem um convite pendente.');
            setLoadingSendInvite(false);
            return
        }

        getUserByEmail(userEmail)
            .then((response: any) => {

                if (!response) {
                    toast.error('Usuário não encontrado.');
                    setLoadingSendInvite(false);
                    return
                }

                setUserName(response.name)
                const invite = {
                    status: 'pending',
                    id: new Date().getTime(),
                    user_uuid_from: JSON.parse(localStorage.getItem(USER_DATA) || '{}').uid,
                    user_uuid_to: response.uid,
                    created_at: new Date().toISOString(),
                    user_to_name: response.name,
                    user_to_email: response.email,
                    project_uuid: id,
                    project_name: currentProject.title
                }

                createInvite(invite)
                    .then(() => {
                        toast.success('Convite enviado com sucesso.');
                        createHistory(mountHistory(currentProject.project_id, ActionTypeEnum.CREATE, "Convite para: " + userName))
                        fetchInvites();
                        sendEmail(response.name)
                    })
            })
            .catch((e) => {
                console.log(e)
            })

    }

    const sendEmail = async (name: string) => {

        const email = getInviteEmail(currentProject.title, name)

        const options = {
            method: 'POST',
            headers: {
                accept: 'application/json',
                'api-key': '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',
                'content-type': 'application/json'
            },
            body: JSON.stringify({
                "sender": {
                    "name": "Qazando",
                    "email": "marketing@qazando.com.br"
                },
                "to": [
                    {
                        "email": userEmail,
                        "name": name
                    }
                ],
                "subject": "Convite Qazando Tools",
                "htmlContent": email
            })
        }

        fetch('https://api.brevo.com/v3/smtp/email', options)
            .then(response => response.json())
            .then(response => console.log(response))
            .then(() => {
                createHistory(mountHistory(currentProject.project_id, ActionTypeEnum.CREATE, "E-mail de conviteenviado para: " + userEmail))
            })
            .catch(err => console.error(err));
    }

    const renderStatus = (status: string) => {

        var color
        var text

        switch (status) {
            case 'pending':
                color = colors.status.yellow;
                text = 'Pendente'
                break;
            case 'accepted':
                color = colors.status.green;
                text = 'Aceito'
                break;
            case 'rejected':
                color = colors.status.red;
                text = 'Rejeitado'
                break;
            default:
                color = colors.status.yellow;
                text = 'Pendente'
                break;
        }

        return (
            <Badge color={color} text={text} />
        )
    }

    return (
        <>
            {loading &&
                <div style={styles.content}>
                    <Loading />
                </div>
            }

            {userOwnerProject && !loading &&
                <div style={styles.content}>
                    <Box>
                        <Typography sx={styles.title}>Usuários do projeto</Typography>

                        <Row style={{ alignItems: 'center', gap: 12, justifyContent: 'start' }}>
                            <Input
                                title='Email do usuário'
                                placeholder='Digite o email do usuário'
                                value={userEmail}
                                onChange={(e) => setUserEmail(e.target.value)}
                                style={{ minWidth: 400 }}
                                required
                            />
                            <ButtonLoading
                                label={'Enviar convite'}
                                onClick={sendInvite}
                                style={{ width: 200, height: 55, marginTop: 36 }}
                                loading={loadingSendInvite}
                            />
                        </Row>

                        <TableContainer component={Paper} sx={{ mt: 6 }}>
                            <Table >
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: colors.background }}>
                                        <TableCell align="center">Nome</TableCell>
                                        <TableCell align="center">Email</TableCell>
                                        <TableCell align="center">Data</TableCell>
                                        <TableCell align="center">Situação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invites && invites.map((invite: any) => (
                                        <TableRow key={invite.id}>
                                            <TableCell align="center">{invite.user_to_name}</TableCell>
                                            <TableCell align="center">{invite.user_to_email}</TableCell>
                                            <TableCell align="center">{formatDatePtBr(new Date(invite.created_at))}</TableCell>
                                            <TableCell align="center" style={{ display: 'flex', justifyContent: 'center' }}>
                                                {renderStatus(invite.status)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {invites.length === 0 &&
                                <EmptyState text="Nenhum convite enviado." />
                            }
                        </TableContainer>
                    </Box>
                </div>
            }
        </>
    );
};
const styles: any = {
    content: {
        backgroundColor: colors.white,
        marginTop: 50,
        marginLeft: 12,
        marginRight: 12
    },

    title: {
        fontSize: 18,
        fontWeight: 600,
        color: '#1A1A1AFF',
        marginTop: 6
    },

}